import React from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { FlatList, View } from 'react-native'
import { DateData } from 'react-native-calendars'
import {
  EhrAppointmentKind,
  EhrAppointmentRecurrenceCadence,
  EhrSlot,
  UiInlineAlert,
} from '@src/types'
import { InlineAlert } from '@src/components/InlineAlert'
import { Button } from '@components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useGoBack } from '@src/utils/navigation'
import { AllCoachProfilesQuery } from '@src/graphql/fetchAllCoachProfiles.generated'
import { SchedulingCalendar } from './SchedulingCalendar'
import { TimeSlot } from './CallScheduling/TimeSlot'
import { LoadingTimeSlot } from './CallScheduling/LoadingTimeSlot'
import { CoachInfo } from './NutritionistSwitch/CoachCard/CoachInfo'
import { SchedulingOptions } from './CallScheduling/SchedulingOptions'
import { AppointmentSchedulingLoadingContent } from './AppointmentSchedulingLoadingContent'

const renderLoadingItem = () => <LoadingTimeSlot />

interface AppointmentSchedulingProps {
  coachProfile?: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0]
  errorMessage?: string
  onConfirmSlot: (params: {
    selectedSlot: EhrSlot
    appointmentKind: EhrAppointmentKind
    cadence: EhrAppointmentRecurrenceCadence
  }) => void
  selectedSlot: EhrSlot | null
  setSelectedSlot: (slot: EhrSlot | null) => void
  appointmentKind: EhrAppointmentKind
  setAppointmentKind: (kind: EhrAppointmentKind) => void
  cadence: EhrAppointmentRecurrenceCadence
  setCadence: (cadence: EhrAppointmentRecurrenceCadence) => void
  selectedDate: string | null
  setSelectedDate: (date: string) => void
  selectedMonth: string | null
  onMonthChange?: (date: DateData) => void
  hasFetchedAnyMonth: boolean
  loadingDates: boolean
  loadingDatesError: boolean
  availableDates: string[]
  availableSlots: EhrSlot[]
  fetchingSlots: boolean
  showSchedulingOptions?: boolean
  timeZone?: string
  showCoachProfile?: boolean
  cadenceOptions: { value: EhrAppointmentRecurrenceCadence; text: string }[]
}

export const AppointmentScheduling = (props: AppointmentSchedulingProps) => {
  const styles = useStyleSheet(themedStyles)
  const goBack = useGoBack()

  const insets = useSafeAreaInsets()

  const {
    coachProfile,
    errorMessage,
    onConfirmSlot,
    selectedSlot,
    setSelectedSlot,
    appointmentKind,
    setAppointmentKind,
    cadence,
    setCadence,
    selectedDate,
    selectedMonth,
    setSelectedDate,
    onMonthChange,
    hasFetchedAnyMonth,
    loadingDates,
    loadingDatesError,
    availableDates,
    availableSlots,
    fetchingSlots,
    showSchedulingOptions,
    cadenceOptions,
    timeZone,
    showCoachProfile = true,
  } = props

  const onDayPress = (day: DateData) => {
    setSelectedDate(day.dateString)
  }

  const confirmSlot = () => {
    if (!selectedSlot) {
      return
    }

    onConfirmSlot({
      selectedSlot,
      appointmentKind,
      cadence,
    })
  }

  const renderSlotItem = ({ item: slot }: { item: EhrSlot }) => {
    return (
      <TimeSlot
        onSelect={setSelectedSlot}
        onConfirm={confirmSlot}
        slot={slot}
        isSelected={slot.time === selectedSlot?.time}
      />
    )
  }

  const showInitialLoadingState =
    loadingDates && !hasFetchedAnyMonth && availableDates.length === 0 && !selectedDate

  if (loadingDatesError) {
    return (
      <View style={styles.errorContainer}>
        <InlineAlert category={UiInlineAlert.Warning} message="Can't load available dates" />
        <Button type="primary" size="block" accessibilityLabel="Go Back" onPress={goBack}>
          Go Back
        </Button>
      </View>
    )
  }

  if (showInitialLoadingState) {
    return (
      <AppointmentSchedulingLoadingContent
        showSchedulingOptions={showSchedulingOptions}
        showCoachProfile={showCoachProfile && !!coachProfile}
      />
    )
  }

  const timeZoneMessage = !coachProfile && timeZone ? `Timezone: ${timeZone}` : undefined
  const alertMessage =
    cadence !== EhrAppointmentRecurrenceCadence.OneTime
      ? "We'll auto-schedule sessions to keep your next four booked"
      : timeZoneMessage

  return (
    <FlatList
      style={styles.slotsList}
      data={fetchingSlots ? Array(3).fill(0) : availableSlots}
      ListHeaderComponent={
        <>
          {errorMessage && (
            <InlineAlert
              style={styles.alert}
              category={UiInlineAlert.Warning}
              message={errorMessage}
            />
          )}
          {showSchedulingOptions && (
            <View style={!alertMessage && styles.schedulingOptionsSpacing}>
              <SchedulingOptions
                appointmentKind={appointmentKind}
                setAppointmentKind={setAppointmentKind}
                cadence={cadence}
                setCadence={setCadence}
                cadenceOptions={cadenceOptions}
              />
            </View>
          )}
          {alertMessage && (
            <InlineAlert
              style={styles.alert}
              category={UiInlineAlert.Neutral}
              message={alertMessage}
            />
          )}
          <SchedulingCalendar
            availableDates={availableDates}
            selectedDate={selectedDate}
            selectedMonth={selectedMonth}
            onDayPress={onDayPress}
            onMonthChange={onMonthChange}
          />
          {showCoachProfile && coachProfile && (
            <View style={styles.coachInfoContainer}>
              <CoachInfo coachProfile={coachProfile} />
            </View>
          )}
        </>
      }
      renderItem={fetchingSlots ? renderLoadingItem : renderSlotItem}
      contentContainerStyle={{ paddingBottom: insets.bottom, gap: 8 }}
    />
  )
}

const themedStyles = StyleService.create({
  alert: {
    marginVertical: 8,
  },
  schedulingOptionsSpacing: {
    marginBottom: 8,
  },
  slotsList: {
    flex: 1,
    paddingHorizontal: 16,
    marginTop: 8,
  },
  coachInfoContainer: {
    padding: 8,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 16,
  },
})
